import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'

import type { WinnerImportCategories, WinnerImportCategoryGroup } from './winner-import.api'
import { formatEuro } from './winner-import-progress'
import {
  categoryOptionsFor, choiceOf, CREATE_VALUE, NONE_VALUE, selectValueOf, type CategoryChoice,
} from './winner-categories'

type Props = {
  categories: WinnerImportCategories
  choices: Record<string, CategoryChoice>
  onChange: (key: string, choice: CategoryChoice) => void
}

/**
 * SOLY-IMPORT-003 S08 — « which of my categories is this Winner group? », asked once per group.
 * Each row is prefilled; the answer is remembered at the creation, so the next import asks nothing.
 */
export function WinnerCategoriesBlock({ categories, choices, onChange }: Props) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Chaque groupe Winner devient une section du devis. Votre choix est retenu pour les prochains imports.
      </p>
      <ul className="divide-y rounded-lg border">
        {categories.groups.map((group) => (
          <GroupRow
            key={group.key}
            group={group}
            categories={categories}
            choice={choices[group.key] ?? { mode: 'none' }}
            onChange={(choice) => onChange(group.key, choice)}
          />
        ))}
      </ul>
    </div>
  )
}

function GroupRow({ group, categories, choice, onChange }: {
  group: WinnerImportCategoryGroup
  categories: WinnerImportCategories
  choice: CategoryChoice
  onChange: (choice: CategoryChoice) => void
}) {
  const name = group.groupName ?? 'Lignes sans groupe Winner'
  const testKey = group.groupNumber ?? 'none'
  const chosenHidden = choice.mode === 'category' && categories.options.some((o) => o.id === choice.categoryId && o.isHidden)
  const createLabel = (choice.mode === 'create' && choice.newLabel.trim()) || group.newLabel || group.groupName

  return (
    <li className="grid gap-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_16rem] sm:items-center" data-testid={`winner-import-category-row-${testKey}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{name}</span>
          {group.kind === 'mapped' && !group.renamedFrom && <Badge variant="secondary">Déjà associé</Badge>}
          {group.renamedFrom && (
            <Badge variant="outline" className="border-amber-300 text-amber-800" data-testid={`winner-import-category-renamed-${testKey}`}>
              Renommé dans Winner (était « {group.renamedFrom} »)
            </Badge>
          )}
          {chosenHidden && (
            <Badge variant="outline" className="border-amber-300 text-amber-800">Catégorie masquée</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {group.lineCount} ligne{group.lineCount > 1 ? 's' : ''} · {formatEuro(group.totalExVat)} HTVA
          {!group.groupNumber && ' · choix valable pour cet import seulement'}
        </p>
      </div>

      <div className="space-y-2">
        <Select value={selectValueOf(choice)} onValueChange={(value) => onChange(choiceOf(value, group, choice))}>
          <SelectTrigger data-testid={`winner-import-category-select-${testKey}`} aria-label={`Catégorie pour ${name}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categoryOptionsFor(categories.options, choice).map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.isHidden ? `${option.label} (masquée)` : option.label}
              </SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value={CREATE_VALUE}>
              {createLabel ? `Créer « ${createLabel} »` : 'Créer une catégorie'}
            </SelectItem>
            <SelectItem value={NONE_VALUE}>Sans catégorie</SelectItem>
          </SelectContent>
        </Select>
        {choice.mode === 'create' && (
          <Input
            value={choice.newLabel}
            maxLength={256}
            onChange={(e) => onChange({ mode: 'create', newLabel: e.target.value })}
            placeholder="Nom de la nouvelle catégorie"
            aria-label={`Nom de la nouvelle catégorie pour ${name}`}
            data-testid={`winner-import-category-new-label-${testKey}`}
          />
        )}
      </div>
    </li>
  )
}
